import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { Box } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';

import { useRoute } from 'hooks/navigation/use-route';
import { routes } from 'navigation/routing';
import { logQuestEvent } from 'state/amplitude';
import { CustomEventNames } from 'state/amplitude/constants';

import { useCmsQuestDetailLazyQuery } from './cms-hooks/use-cms-quest-detail';
import { useActivateQuest } from './loyalty-quest/use-activate-quest';
import { canActivateQuest, isQuestCompleted } from './loyalty-quest/utils';
import { QuestCard } from './quest-card';
import type { IQuestCardWrapperProps } from './types';
import { useParseQuestCardData } from './use-parse-quest-card-data';
import { getActiveTierDescription } from './use-parse-quest-card-data/utils';
import { getProductRequirementCmsIds, isPurchaseQuest } from './utils';

export const QuestCardWrapper = ({
  cmsQuestCard,
  quest,
  cmsQuestCardUI,
}: IQuestCardWrapperProps) => {
  const questRequirementCmsIds = useMemo(() => getProductRequirementCmsIds(quest), [quest]);
  const [
    cmsQuestDetailQuery,
    { loadingCmsQuestDetail, cmsQuestDetail, refetch: refetchCmsQuestDetail },
  ] = useCmsQuestDetailLazyQuery({ cmsId: quest.cmsId, questRequirementCmsIds });

  const {
    image,
    imageDescription,
    title,
    description,
    buttonText,
    linkText,
  } = useParseQuestCardData(quest, cmsQuestCard, cmsQuestCardUI, cmsQuestDetail);
  const { navigate } = useRouter();

  const { pathname } = useRoute<{ pathname: string }>();
  const loadingQuestDetail = loadingCmsQuestDetail;

  const [activateQuest, { loading: isActivating }] = useActivateQuest({ questId: quest.id });
  const isPurchase = useMemo(() => !!(cmsQuestDetail && isPurchaseQuest(cmsQuestDetail)), [
    cmsQuestDetail,
  ]);

  const callToActionCard = useCallback(async () => {
    logQuestEvent(
      {
        name: CustomEventNames.QUEST_DETAILS_VIEW,
        quest,
        cmsQuestCard,
      },
      { skipLoggingToBraze: true }
    );
    if (isPurchase) {
      if (canActivateQuest(quest.status)) {
        try {
          await activateQuest();
        } catch {
          return;
        }
      }
      navigate(routes.menu);
      return;
    }
    navigate(`${routes.loyaltyChallengeDetails}?previousPath=${pathname}`);
  }, [activateQuest, cmsQuestCard, isPurchase, navigate, pathname, quest]);

  const onPressCardHandler = useCallback(() => {
    logQuestEvent(
      {
        name: CustomEventNames.QUEST_DETAILS_VIEW,
        quest,
        cmsQuestCard,
      },
      { skipLoggingToBraze: true }
    );
    navigate(`${routes.loyaltyChallengeDetails}?previousPath=${pathname}`);
    cmsQuestDetailQuery();
  }, [cmsQuestCard, cmsQuestDetailQuery, navigate, pathname, quest]);

  const onShowTermsPressHandler = useCallback(() => {
    logQuestEvent(
      {
        name: CustomEventNames.QUEST_DETAILS_VIEW,
        quest,
        cmsQuestCard,
      },
      { skipLoggingToBraze: true }
    );
    navigate(`${routes.questTerms}?cmsId=${cmsQuestCard._id}`);
  }, [cmsQuestCard, navigate, quest]);

  useEffect(() => {
    if (!loadingQuestDetail && pathname.includes(routes.loyaltyActivateQuest) && quest.id) {
      // If quest exists open the drawer and activate challenge
      callToActionCard();
    }
  }, [callToActionCard, loadingQuestDetail, pathname, quest.id]);

  useEffect(() => {
    if (!cmsQuestDetail && !loadingCmsQuestDetail) {
      refetchCmsQuestDetail();
    }
  }, [cmsQuestDetail, loadingCmsQuestDetail, refetchCmsQuestDetail]);

  return (
    <>
      {!loadingCmsQuestDetail && (
        <Box alignItems="center" marginBottom="$1_5">
          <QuestCard
            image={image}
            altText={imageDescription}
            title={title}
            description={description}
            howToRedeem={getActiveTierDescription(quest, cmsQuestDetail)}
            callToAction={callToActionCard}
            callToActionText={buttonText}
            linkText={linkText}
            // Purchase quests have no card-level navigation; omit the handler so
            // the card stays non-interactive (no pointer cursor on web).
            onPressCard={isPurchase ? undefined : onPressCardHandler}
            onShowTermsPress={onShowTermsPressHandler}
            isQuestCompleted={isQuestCompleted(quest.status)}
            isActivating={isActivating}
            steps={quest?.steps}
            status={quest?.status}
            endDate={quest?.endDate ?? ''}
          />
        </Box>
      )}
    </>
  );
};
